/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Component, EventEmitter, Output, OnInit, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    AuctionCatalogsService,
    AuctionDetail,
    AuctionStatus,
    BasicListResponse,
    ListResponse,
    NotificationsService,
    Reminder,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { ServerClockService } from '../../services/server-clock.service';

const REMINDER_OPTION_OPENING: string = 'opening';
const REMINDER_OPTION_CLOSING: string = 'closing';
const REMINDER_OPTION_BOTH: string = 'both';
const REMINDER_TYPE_OPENING: number = 1;
const REMINDER_TYPE_CLOSING: number = 2;

@Component({
    selector: 'vb-scheduler-dialog',
    templateUrl: './scheduler-dialog.component.html',
    styleUrls: ['./scheduler-dialog.component.scss'],
})
export class SchedulerDialogComponent implements OnInit {
    @Output() closeReminders: EventEmitter<void> = new EventEmitter<void>();
    @Input() auction!: AuctionDetail;
    @Output() remindersChange: EventEmitter<Reminder[]> = new EventEmitter<
        Reminder[]
    >();

    public reminders: Reminder[] = [];
    public isEditing: boolean = false;
    public isAdding: boolean = false;
    public formValid: boolean = false;
    public alertTimes: any[] = [];
    /**
     * Every reminder time, each tagged with `unavailable` — the ones already
     * past or already spoken for stay on screen, crossed out, so the full
     * set of choices reads at a glance.
     */
    public alertTimeOptions: any[] = [];
    public allTimesUsed = false;
    public isLoading: boolean = true;
    public isAuctionStarted: boolean = false;
    public selectedOption: string = REMINDER_OPTION_CLOSING;
    public reminderOptions: any[] = [
        {
            label: this.translate.instant('Notifications.whenAuctionStarts'),
            value: REMINDER_OPTION_OPENING,
        },
        {
            label: this.translate.instant('Notifications.whenAuctionEnds'),
            value: REMINDER_OPTION_CLOSING,
        },
        {
            label: this.translate.instant('Notifications.both'),
            value: REMINDER_OPTION_BOTH,
        },
    ];
    private successToastId: number = 0;
    private fromDatetime: string = '';
    /**
     * Anchor for "before close" reminders. With staggered closings the
     * auction is effectively over for bidding once lots *start* closing, so
     * closing reminders target firstLotCloseTime — matching the "First Lot
     * Closes:" countdown shown everywhere else. toDatetime (last lot close)
     * is only the fallback when the backend supplied no first-lot time.
     */
    private closeTargetDatetime: string = '';
    private openingOptionText: string = this.translate.instant(
        'Notifications.beforeAuctionStartText',
    );
    private closingOptionText: string = this.translate.instant(
        'Notifications.beforeAuctionEndsText',
    );
    private bothOptionText: string = this.translate.instant(
        'Notifications.bothOptionText',
    );

    public currentReminder: Reminder = {
        notifyAtLabel: undefined,
        email: false,
        sms: false,
    };
    public originalReminder: Reminder = {
        notifyAtLabel: undefined,
        email: false,
        sms: false,
    };

    constructor(
        private notificationsService: NotificationsService,
        private auctionService: AuctionCatalogsService,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private serverClock: ServerClockService,
    ) {}

    ngOnInit(): void {
        this.resetCurrentReminder();

        this.fromDatetime = this.auction.fromDatetime ?? '';
        this.closeTargetDatetime =
            this.auction.firstLotCloseTime ?? this.auction.toDatetime ?? '';
        this.isAuctionStarted = this.auction.status === AuctionStatus.Open;

        this.auctionService.getListByGroup('reminders').subscribe({
            next: (res: ListResponse) => {
                const basicList: BasicListResponse[] =
                    res.data as BasicListResponse[];
                if (res.data.length > 0) {
                    this.prepareAlertTimes(basicList);
                }
                this.getReminders();
            },
            error: () => {
                this.isLoading = false;
            },
        });
        this.checkFormValid();
    }

    prepareAlertTimes(response: BasicListResponse[]): void {
        response.forEach((item: BasicListResponse) => {
            this.alertTimes.push({
                value: item.value.toString(),
            });
        });
    }

    saveReminder(): void {
        if (!this.isNotifyAtValid(this.currentReminder.notifyAtLabel)) {
            this.snackbarService.error(
                this.translate.instant(
                    'Notifications.errorMessageReminderTimePassed',
                ),
            );
            this.resetCurrentReminder();
            this.filterAlertTimes();
            return;
        }

        const reminderTypeIds: number[] =
            this.selectedOption === REMINDER_OPTION_BOTH
                ? [REMINDER_TYPE_OPENING, REMINDER_TYPE_CLOSING]
                : this.selectedOption === REMINDER_OPTION_OPENING
                  ? [REMINDER_TYPE_OPENING]
                  : [REMINDER_TYPE_CLOSING];

        reminderTypeIds.forEach((reminderTypeId: number) => {
            this.notificationsService
                .createAuctionReminder({
                    reminderTypeId,
                    notifyAt: new Date(
                        this.getTargetTime(reminderTypeId) -
                            Number(this.currentReminder.notifyAtLabel),
                    ).toISOString(),
                    notifyAtLabel: this.currentReminder.notifyAtLabel!,
                    notificationSent: false,
                    proxyId: this.auction.id!,
                    sms: this.currentReminder.sms ?? false,
                    email: this.currentReminder.email ?? false,
                })
                .subscribe({
                    next: () => {
                        this.snackbarService.dismiss(this.successToastId);
                        this.successToastId = this.snackbarService.success(
                            this.translate.instant(
                                'Notifications.successReminderSet',
                            ),
                        );
                        this.getReminders();
                    },
                    error: () => {
                        this.snackbarService.error(
                            this.translate.instant(
                                'Notifications.errorReminderSet',
                            ),
                        );
                        this.getReminders();
                    },
                });
        });
        this.resetCurrentReminder();
        this.isEditing = false;
        this.filterAlertTimes();
        this.sortReminders();
    }

    getReminders(): void {
        this.reminders = [];
        this.notificationsService
            .getRemindersByAccountMappingsId(
                1,
                -1,
                this.auction.id ?? undefined,
            )
            .subscribe({
                next: (res: ListResponse) => {
                    if (res.data && res.data.length > 0) {
                        res.data.forEach((reminder: Reminder) => {
                            this.reminders.push(
                                this.normalizeReminder(reminder),
                            );
                        });
                    }
                    this.filterAlertTimes();
                    this.sortReminders();
                    this.remindersChange.emit(this.reminders);
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                },
            });
    }

    updateReminder(): void {
        if (!this.isNotifyAtValid(this.currentReminder.notifyAtLabel)) {
            this.snackbarService.error(
                this.translate.instant(
                    'Notifications.errorMessageReminderTimePassed',
                ),
            );
            this.resetCurrentReminder();
            this.resetOriginalReminder();
            this.filterAlertTimes();
            this.isEditing = false;
            return;
        }

        this.notificationsService
            .putReminderById(this.currentReminder.id!, {
                reminderTypeId: this.currentReminder.reminderTypeId,
                notifyAt: new Date(
                    this.getTargetTime(this.currentReminder.reminderTypeId!) -
                        Number(this.currentReminder.notifyAtLabel),
                ).toISOString(),
                notifyAtLabel: this.currentReminder.notifyAtLabel!,
                notificationSent: false,
                proxyId: this.auction.id!,
                sms: this.currentReminder.sms,
                email: this.currentReminder.email,
            })
            .subscribe({
                next: () => {
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(
                            'Notifications.successReminderUpdated',
                        ),
                    );
                    this.getReminders();
                },
                error: () => {
                    this.snackbarService.error(
                        this.translate.instant(
                            'Notifications.errorReminderUpdated',
                        ),
                    );
                    this.getReminders();
                },
            });

        this.resetCurrentReminder();
        this.resetOriginalReminder();
        this.filterAlertTimes();
        this.isEditing = false;
    }

    deleteReminder(reminder: Reminder): void {
        this.notificationsService.deleteReminderById(reminder.id!).subscribe({
            next: () => {
                this.snackbarService.dismiss(this.successToastId);
                this.successToastId = this.snackbarService.success(
                    this.translate.instant(
                        'Notifications.successReminderDeleted',
                    ),
                );
                this.getReminders();
                this.filterAlertTimes();
            },
            error: () => {
                this.snackbarService.error(
                    this.translate.instant(
                        'Notifications.errorReminderDeleted',
                    ),
                );
                this.getReminders();
            },
        });
    }

    resetCurrentReminder(): void {
        this.currentReminder = {
            notifyAtLabel: undefined,
            email: true,
            sms: true,
        };
        this.checkFormValid();
    }

    resetOriginalReminder(): void {
        this.originalReminder = {
            notifyAtLabel: undefined,
            email: true,
            sms: true,
        };
        this.checkFormValid();
    }

    editReminder(reminder: Reminder): void {
        this.currentReminder = { ...reminder };
        this.originalReminder = { ...reminder };
        this.isEditing = true;
        this.selectedOption =
            reminder.reminderTypeId === REMINDER_TYPE_OPENING
                ? REMINDER_OPTION_OPENING
                : REMINDER_OPTION_CLOSING;

        this.filterAlertTimes();
        this.checkFormValid();
    }

    cancelEdit(): void {
        this.resetCurrentReminder();
        this.resetOriginalReminder();
        this.isEditing = false;
        this.selectedOption = REMINDER_OPTION_CLOSING;
        this.filterAlertTimes();
    }
    convertMillisecondsToTime(value: string | number): string {
        const milliseconds: number = Number(value);
        const minutes: number = milliseconds / (1000 * 60);

        if (minutes < 60) {
            return `${Math.round(minutes)} min${Math.round(minutes) !== 1 ? 's' : ''}`;
        }

        const hours: number = minutes / 60;
        if (hours < 24) {
            return `${Math.round(hours)} hour${Math.round(hours) !== 1 ? 's' : ''}`;
        }

        const days: number = hours / 24;
        return `${Math.round(days)} day${Math.round(days) !== 1 ? 's' : ''}`;
    }

    filterAlertTimes(): void {
        const timeUntilTarget: number =
            this.getTimeUntilActiveTargetInMilliseconds();
        // A time is unavailable when it has already passed, or when an
        // existing reminder of the active type already claims it (the one
        // being edited excepted). Unavailable times are kept in the list and
        // rendered struck through rather than dropped, so the chips don't
        // shuffle as time runs out or as the open/close option changes.
        this.alertTimeOptions = this.alertTimes.map((time: any) => ({
            ...time,
            unavailable:
                Number(time.value) >= timeUntilTarget ||
                this.reminders.some(
                    (reminder: Reminder) =>
                        this.isActiveReminderType(reminder) &&
                        reminder.notifyAtLabel === time.value &&
                        !(
                            this.isEditing &&
                            reminder.notifyAtLabel ===
                                this.originalReminder.notifyAtLabel
                        ),
                ),
        }));

        this.allTimesUsed = this.alertTimeOptions.every(
            (time: any) => time.unavailable,
        );
    }

    getTimeUntilAuctionOpensInMilliseconds(): number {
        const auctionOpenTime: number = new Date(this.fromDatetime).getTime();
        const currentTime: number = this.serverClock.now();
        const timeDifferenceInMilliseconds: number =
            auctionOpenTime - currentTime;
        return timeDifferenceInMilliseconds;
    }

    getTimeUntilAuctionClosesInMilliseconds(): number {
        const closeTargetTime: number = new Date(
            this.closeTargetDatetime,
        ).getTime();
        const currentTime: number = this.serverClock.now();
        const timeDifferenceInMilliseconds: number =
            closeTargetTime - currentTime;
        return timeDifferenceInMilliseconds;
    }

    isNotifyAtValid(notifyAtLabel: string | undefined): boolean {
        return (
            !!notifyAtLabel &&
            Number(notifyAtLabel) <
                this.getTimeUntilActiveTargetInMilliseconds()
        );
    }

    checkFormValid(): void {
        this.formValid =
            !!this.currentReminder.notifyAtLabel &&
            this.isNotifyAtValid(this.currentReminder.notifyAtLabel) &&
            ((this.currentReminder.email ?? false) ||
                (this.currentReminder.sms ?? false)) &&
            !this.isSameAsOriginal();
    }

    selectAlertTime(value: string): void {
        this.currentReminder.notifyAtLabel = value;
        this.checkFormValid();
    }

    toggleMethod(method: 'email' | 'sms'): void {
        this.currentReminder[method] = !this.currentReminder[method];
        this.checkFormValid();
    }

    methodLabel(reminder: Reminder): string {
        if (reminder.email && reminder.sms) return 'Email and text';
        return reminder.email ? 'Email' : 'Text';
    }

    isSameAsOriginal(): boolean {
        if (!this.originalReminder) {
            return false;
        }

        return (
            this.currentReminder.notifyAtLabel ===
                this.originalReminder.notifyAtLabel &&
            this.currentReminder.email === this.originalReminder.email &&
            this.currentReminder.sms === this.originalReminder.sms
        );
    }

    getNotificationText(): string {
        const parts: any[] = [];
        if (this.currentReminder.sms) {
            parts.push('<strong>text</strong>');
        }
        if (this.currentReminder.email) {
            parts.push('<strong>email</strong>');
        }
        const notifyAtText: string = `${this.convertMillisecondsToTime(
            this.currentReminder.notifyAtLabel ?? '',
        )} ${this.getActiveOptionText()}`;
        return `We'll send you a ${parts.join(' and ')} notification <strong>${notifyAtText.toLowerCase()}</strong> to remind you about this auction.`;
    }

    sortReminders(): void {
        this.reminders.sort((a: Reminder, b: Reminder) => {
            const aValue: number = parseInt(a.notifyAtLabel || '0', 10);
            const bValue: number = parseInt(b.notifyAtLabel || '0', 10);
            return aValue - bValue;
        });
    }

    closeComponent(): void {
        this.closeReminders.emit();
    }

    // --- Auction-specific: opening/closing/both reminder types ---------------

    onOptionSelect(option: string): void {
        this.selectedOption = option;
        this.filterAlertTimes();
        if (
            this.currentReminder.notifyAtLabel &&
            !this.alertTimeOptions.some(
                (time: any) =>
                    time.value === this.currentReminder.notifyAtLabel &&
                    !time.unavailable,
            )
        ) {
            this.currentReminder.notifyAtLabel = undefined;
        }
        this.checkFormValid();
    }

    beforeLabel(reminder: Reminder): string {
        return reminder.reminderTypeId === REMINDER_TYPE_OPENING
            ? 'before open'
            : 'before lots start closing';
    }

    get alertTimeFieldLabel(): string {
        const option: string = this.getActiveOption();
        if (option === REMINDER_OPTION_OPENING) {
            return 'Remind me before open';
        }
        if (option === REMINDER_OPTION_BOTH) {
            return 'Remind me before open & lots start closing';
        }
        return 'Remind me before lots start closing';
    }

    private getActiveOption(): string {
        if (this.isEditing) {
            return this.currentReminder.reminderTypeId === REMINDER_TYPE_OPENING
                ? REMINDER_OPTION_OPENING
                : REMINDER_OPTION_CLOSING;
        }
        return this.selectedOption;
    }

    private getActiveOptionText(): string {
        const option: string = this.getActiveOption();
        if (option === REMINDER_OPTION_OPENING) {
            return this.openingOptionText;
        }
        if (option === REMINDER_OPTION_BOTH) {
            return this.bothOptionText;
        }
        return this.closingOptionText;
    }

    private getTargetTime(reminderTypeId: number): number {
        return reminderTypeId === REMINDER_TYPE_OPENING
            ? new Date(this.fromDatetime).getTime()
            : new Date(this.closeTargetDatetime).getTime();
    }

    private getTimeUntilActiveTargetInMilliseconds(): number {
        const option: string = this.getActiveOption();
        // "Both" must fit before the earlier target — the opening time.
        if (
            option === REMINDER_OPTION_OPENING ||
            option === REMINDER_OPTION_BOTH
        ) {
            return this.getTimeUntilAuctionOpensInMilliseconds();
        }
        return this.getTimeUntilAuctionClosesInMilliseconds();
    }

    private isActiveReminderType(reminder: Reminder): boolean {
        if (this.isEditing) {
            return (
                reminder.reminderTypeId === this.currentReminder.reminderTypeId
            );
        }
        if (this.selectedOption === REMINDER_OPTION_OPENING) {
            return reminder.reminderTypeId === REMINDER_TYPE_OPENING;
        }
        if (this.selectedOption === REMINDER_OPTION_CLOSING) {
            return reminder.reminderTypeId === REMINDER_TYPE_CLOSING;
        }
        return (
            reminder.reminderTypeId === REMINDER_TYPE_OPENING ||
            reminder.reminderTypeId === REMINDER_TYPE_CLOSING
        );
    }

    // Legacy auction reminders stored a human label ("15 mins") in
    // notifyAtLabel; the offset in milliseconds is recoverable from notifyAt.
    private normalizeReminder(reminder: Reminder): Reminder {
        if (Number.isFinite(Number(reminder.notifyAtLabel))) {
            return reminder;
        }
        const offsetMs: number =
            this.getTargetTime(reminder.reminderTypeId ?? -1) -
            new Date(reminder.notifyAt ?? 0).getTime();
        return { ...reminder, notifyAtLabel: offsetMs.toString() };
    }
}
